import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, CircularProgress, Typography } from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import apiService from '../../../services/apiService';

/**
 * Server-authoritative duration estimate, shown BEFORE submit (#937/#938).
 *
 * The reporter of SUP-2026-00005 could not tell how long an observation would take, so he oversized
 * every fixed-time window "to be safe" — which is what blocked the rest of the night from other users.
 * He asked directly: "I assume platesolves and calibration does not take time from the sequence? Or
 * does it?" It does, and this component says so.
 *
 * The number comes from the server (FlexDurationEstimator), not the client: the old client-side
 * estimator is wrong and the server overrides it for every type but `monitoring` anyway.
 */

const DEBOUNCE_MS = 400;

/** "1h 05m" / "11m" */
export function formatMinutes(mins) {
  if (mins == null) return '—';
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h > 0 ? `${h}h ${String(m).padStart(2, '0')}m` : `${m}m`;
}

/** Renders the "31m exposures · 8m overhead · 6m setup · 1m buffer" line + provenance. */
export function BreakdownLine({ breakdown }) {
  if (!breakdown) return null;
  const parts = [
    breakdown.exposure_minutes != null && `${breakdown.exposure_minutes}m exposures`,
    breakdown.overhead_minutes != null && `${breakdown.overhead_minutes}m overhead`,
    breakdown.setup_minutes != null && `${breakdown.setup_minutes}m setup`,
    breakdown.buffer_minutes ? `${breakdown.buffer_minutes}m buffer` : null,
  ].filter(Boolean);

  return (
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
      {parts.join(' · ')}
      {breakdown.source === 'measured'
        ? ' (measured from this observatory’s realized durations)'
        : ' (estimated — not enough history yet)'}
    </Typography>
  );
}

/**
 * @param targets            react-hook-form targets array
 * @param assignedTo         observatory id (the overhead profile is per-observatory)
 * @param globalRepeatCount  observation-level repeat
 * @param autofocusOptions   so the estimate can charge for the initial autofocus
 * @param onEstimate         (result | null) => void — lets the parent auto-size a window
 * @param showWindowHint     append "reserves Nm including grace" (fixed-start mode)
 */
const DurationEstimate = ({
  targets,
  assignedTo,
  globalRepeatCount,
  autofocusOptions,
  onEstimate,
  showWindowHint = false,
}) => {
  const [state, setState] = useState({ loading: false, error: null, result: null });
  const onEstimateRef = useRef(onEstimate);
  onEstimateRef.current = onEstimate;

  // The target array churns on every keystroke in the exposure step; key the effect on a stable
  // signature rather than object identity, and debounce what survives that.
  const signature = JSON.stringify({
    assignedTo,
    globalRepeatCount,
    initialAf: autofocusOptions?.initial_autofocus ?? null,
    afEnabled: autofocusOptions?.enabled ?? null,
    t: (targets || []).map((t) => ({
      r: t.sequence_repeat_count,
      e: (t.exposure_configs || []).map((c) => [c.exposure_time, c.exposure_count, c.filter]),
    })),
  });

  useEffect(() => {
    const hasWork = Array.isArray(targets)
      && targets.some((t) => (t.exposure_configs || []).some((c) => c.exposure_count > 0));

    if (!assignedTo || !hasWork) {
      setState({ loading: false, error: null, result: null });
      onEstimateRef.current?.(null);
      return undefined;
    }

    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: null }));

    const timer = setTimeout(async () => {
      try {
        const result = await apiService.estimateObservationDuration({
          targets,
          assignedTo,
          globalRepeatCount,
          autofocusOptions,
        });
        if (cancelled) return;
        setState({ loading: false, error: null, result });
        onEstimateRef.current?.(result);
      } catch (err) {
        if (cancelled) return;
        // Never block the form on an estimate failure — it is advisory.
        setState({ loading: false, error: 'Could not estimate duration', result: null });
        onEstimateRef.current?.(null);
      }
    }, DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature]);

  if (!assignedTo) return null;

  if (state.loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
        <CircularProgress size={16} />
        <Typography variant="body2" color="text.secondary">Estimating duration…</Typography>
      </Box>
    );
  }

  if (state.error) {
    return <Alert severity="warning" sx={{ mt: 1 }}>{state.error}</Alert>;
  }

  if (!state.result) return null;

  const { estimated_minutes: estimate, breakdown, derived_window_minutes: windowMinutes } = state.result;

  return (
    <Alert severity="info" icon={<AccessTimeIcon />} sx={{ mt: 1 }}>
      <Typography variant="body2">
        <strong>Estimated duration: {formatMinutes(estimate)}</strong>
        {showWindowHint && windowMinutes != null && (
          <> — reserves {formatMinutes(windowMinutes)} of telescope time, then releases it</>
        )}
      </Typography>
      <BreakdownLine breakdown={breakdown} />
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
        Slew, plate-solve centering and the initial autofocus run inside the window and consume it —
        they are not free. Download, dither, filter changes and periodic autofocus are in “overhead”.
      </Typography>
    </Alert>
  );
};

export default DurationEstimate;
